import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users2, Trophy, Copy, Share2, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function Referrals() {
  const [d, setD] = useState(null);
  const [board, setBoard] = useState([]);

  useEffect(() => {
    api.get("/referrals/dashboard").then((r) => setD(r.data));
    api.get("/referrals/leaderboard").then((r) => setBoard(r.data));
  }, []);

  if (!d) return <div className="text-gray-500">Loading referrals…</div>;

  const fullLink = `${window.location.origin}/signup?ref=${d.referral_code}`;
  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <div data-testid="page-referrals">
      <PageHeader eyebrow="Refer & Earn" title="Share savings, earn together" subtitle="Earn 20% of platform commission on every verified purchase from your direct referrals." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total referrals" value={d.total_referrals} icon={Users2} testId="ref-total" />
        <StatCard label="Active referrals" value={d.active_referrals} icon={Users2} accent="emerald" sublabel="Made first purchase" testId="ref-active" />
        <StatCard label="Total earned" value={fmtMoney(d.total_earnings)} icon={Trophy} accent="amber" testId="ref-earned" />
        <StatCard label="Pending" value={fmtMoney(d.pending_earnings)} accent="sky" testId="ref-pending" />
      </div>

      <Card className="border-emerald-100 shadow-none rounded-2xl gradient-emerald mb-8">
        <CardContent className="p-6 grid lg:grid-cols-2 gap-6 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold mb-2">Your referral code</div>
            <div className="font-display font-bold text-3xl tracking-tight" data-testid="ref-code">{d.referral_code}</div>
            <p className="text-sm text-gray-700 mt-2">Share this code or your unique link. Friends get a personalized AI deal feed instantly.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => copy(d.referral_code)} variant="outline" className="rounded-full" data-testid="copy-code">
                <Copy className="w-4 h-4 mr-2" /> Copy code
              </Button>
              <Button onClick={() => copy(fullLink)} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid="copy-link">
                <Share2 className="w-4 h-4 mr-2" /> Copy link
              </Button>
            </div>
          </div>
          <div>
            <div className="bg-white border border-gray-200 rounded-2xl p-4">
              <div className="text-xs text-gray-500 mb-2">Share via</div>
              <div className="flex flex-wrap gap-2">
                <a href={`mailto:?subject=Earn cashback with LoyaltyCircle AI&body=Join me on LoyaltyCircle AI: ${fullLink}`} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm" data-testid="share-email">
                  <Mail className="w-4 h-4" /> Email
                </a>
                <a href={`sms:?body=Earn cashback on every purchase with LoyaltyCircle AI: ${fullLink}`} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm" data-testid="share-sms">
                  <MessageSquare className="w-4 h-4" /> SMS
                </a>
                <a href={`https://twitter.com/intent/tweet?text=Earning real cashback on everything I buy via @LoyaltyCircleAI ${fullLink}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm">
                  𝕏 Twitter
                </a>
              </div>
              <Input readOnly value={fullLink} className="mt-3 text-xs" />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-gray-200 shadow-none rounded-2xl">
          <CardContent className="p-0">
            <div className="p-5 border-b border-gray-200 font-display font-bold text-base">Your referrals</div>
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr className="text-left">
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Joined</th>
                </tr>
              </thead>
              <tbody>
                {d.referrals.map((r) => (
                  <tr key={r.id} className="border-b border-gray-100" data-testid={`ref-row-${r.id}`}>
                    <td className="px-5 py-3 font-medium">{r.referee?.name || "Pending"}</td>
                    <td className="px-5 py-3 capitalize">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${r.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{r.status}</span>
                    </td>
                    <td className="px-5 py-3 text-gray-500">{new Date(r.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {d.referrals.length === 0 && (
                  <tr><td colSpan="3" className="px-5 py-10 text-center text-gray-500">No referrals yet. Share your link to start earning.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card className="border-gray-200 shadow-none rounded-2xl">
          <CardContent className="p-0">
            <div className="p-5 border-b border-gray-200 font-display font-bold text-base flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-700" /> Top referrers this month
            </div>
            <ul className="divide-y divide-gray-100">
              {board.map((u, i) => (
                <li key={i} className="px-5 py-3 flex items-center justify-between text-sm">
                  <span className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-emerald-50 text-emerald-700 text-xs flex items-center justify-center font-semibold">{i + 1}</span>
                    {u.name}
                  </span>
                  <span className="font-semibold text-gray-900">{fmtMoney(u.total_earnings)}</span>
                </li>
              ))}
              {board.length === 0 && <li className="px-5 py-6 text-center text-gray-500 text-sm">No earners yet.</li>}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card className="border-gray-200 shadow-none rounded-2xl mt-6">
        <CardContent className="p-5 text-xs text-gray-500 leading-relaxed">
          <strong>Compliance:</strong> Referral rewards are paid only on verified retail purchases made by your referred users — never for sign-ups alone. Rewards come from the platform's 50% share of affiliate commissions, not from your referees' cashback. LoyaltyCircle AI does not operate as an MLM.
        </CardContent>
      </Card>
    </div>
  );
}
